import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient, baseURL } from "../utils/const";
import { toast } from "react-toastify";

const CatalogueGallery = () => {
  const { id } = useParams();
  const catalogueName = "Catalogue";
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [viewer, setViewer] = useState(null); // { src, meta }

  const fetchImages = async () => {
    try {
      const res = await apiClient.post(`${baseURL}/user_api/get_gallery.php`, { cat_id: id });
      if (res.data?.message === "radha") {
        setImages(res.data.data || []);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchImages();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.set("cat_id", id);
      const selectedFiles = Array.from(files);
      selectedFiles.forEach((file) => {
        fd.append("image_path[]", file, file.name);
      });
      // Debug: verify all file names included
      try {
        const debugFiles = [];
        for (const [k, v] of fd.entries()) {
          if (v instanceof File) debugFiles.push(`${k}: ${v.name}`);
        }
        // console.log("[CatalogueGallery] uploading files:", debugFiles.join(", "));
      } catch {}

      const res = await apiClient.post(`/insert_catalogue_gallery.php`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.message === "radha") {
        toast.success("Images uploaded!");
        setFiles([]);
        fetchImages();
      } else {
        toast.error(res.data?.message || "Upload failed");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setUploading(false);
    }
  };

  const resolveUrl = (p) => {
    if (!p) return null;
    if (p.startsWith("http")) return p;
    const filePart = p.includes("/") ? p : encodeURIComponent(p);
    return `${baseURL}/gallery/${filePart}`;
  };

  return (
    <>
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl">
          <div className="row mb-1">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center bg-body p-2 rounded shadow-sm">
                <h2 className="mb-0 fw-bold">{catalogueName} • Gallery</h2>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 mb-1">
              <div className="card" style={{ borderRadius: 16, padding: 16 }}>
                <div style={{ fontWeight: 600, color: "var(--bs-body-color)", marginBottom: 12 }}>
                  Add Images
                </div>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 220 }}>
                      <label style={{ fontSize: 12, color: "var(--bs-secondary-color)" }}>Select Images</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        name="image_path[]"
                        className="form-control"
                        onChange={(e) => setFiles(e.target.files)}
                      />
                    </div>
                    <button type="submit" disabled={uploading || !files.length} className="btn btn-primary">
                      {uploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
                <div className="card-header" style={{ borderRadius: "12px 12px 0 0" }}>
                  <h5 className="card-title mb-0 fw-bold">Images</h5>
                </div>
                <div className="card-body">
                  {images?.length ? (
                    <div className="row g-3">
                      {images.map((img, i) => (
                        <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={i}>
                          <img
                            src={resolveUrl(img.image_path || img.path || img.image)}
                            alt="gallery"
                            className="img-fluid rounded border"
                            style={{ cursor: "zoom-in" }}
                            onClick={() =>
                              setViewer({
                                src: resolveUrl(img.image_path || img.path || img.image),
                                meta: {
                                  admin: img.admin_name || img.admin || "—",
                                  status:
                                    String(img.status) === "1" || img.status === true
                                      ? "Active"
                                      : "Inactive",
                                  created: img.created_at || img.date || img.created_on || "—",
                                },
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted">No images yet</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    {viewer && (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        style={{ background: "rgba(0,0,0,0.8)", zIndex: 10000 }}
        onClick={() => setViewer(null)}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setViewer(null);
          }}
          className="position-absolute"
          style={{
            top: 20,
            right: 20,
            background: "rgba(255,255,255,0.9)",
            border: "none",
            borderRadius: "50%",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: 20,
            fontWeight: "bold",
          }}
          title="Close"
        >
          ×
        </button>
        <div
          className="bg-white p-3 rounded shadow"
          style={{ maxWidth: "90%", maxHeight: "90%" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center mb-2">
            <img
              src={viewer.src}
              alt="preview"
              style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }}
            />
          </div>
          <div className="d-flex flex-wrap gap-3 justify-content-between" style={{ fontSize: 14 }}>
            <div>
              <span className="text-muted">Admin: </span>
              <span className="fw-semibold">{viewer.meta.admin}</span>
            </div>
            <div>
              <span className="text-muted">Status: </span>
              <span className={`badge ${viewer.meta.status === "Active" ? "bg-success" : "bg-secondary"}`}>{viewer.meta.status}</span>
            </div>
            <div>
              <span className="text-muted">Created: </span>
              <span className="fw-semibold">{viewer.meta.created}</span>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CatalogueGallery;


